import React, { useEffect, useState } from "react";
import { useLocale, useTranslations } from "../../../contexts/Translation";
import { H2 } from "../../Headers/Headings";
import Section from "../../Section";
import { useApiData } from "../../../contexts/ApiData";
import Button from "../../Button/Button";
import Input, { Select } from "../Input";
import { EmailLang, OrgUser, ValidationError } from "../../../types";
import { useNavigate } from "react-router-dom";
import { useSurvey } from "../../../contexts/Survey";
import { useOrganizationData, useOrganizations, useProfile, useRole } from "../../../contexts/UserProvider";
import GroupAccess from "../GroupAccess";
import { EmailPreview } from "./EmailPreview";
import Invites from "../../Survey/Invites/Invites";
import { Tip } from "../../Tip/Index";
import HeadingWithInfo from "../../../components/HeadingWithInfo/HeadingWithInfo";
import SurveyInfo from "./SurveyInfo";
import getQuestionsForSurveyType from "../../../lib/getQuestionsForSurveyType";
import styles from "./SurveyWizard.module.css";
import formStyles from "../FormStyles.module.css";
import WizardProgress from "./WizardProgress";
import { MAX_INVITATION_TEXT, MAX_SURVEY_NAME } from "../../../lib/textLimits";

type SurveyFormData = {
  id: string;
  endDate: string;
  invitationText: string;
  surveyName: string;
  defaultLang: string;
  accessibleBy: string;
  onBehalfOf: string | null;
  groupsWithAccess?: string[];
  surveyType: "long-term" | "workshop" | "delivery";
  allowAnonymous: boolean;
  showRecommendation: boolean;
};

type SurveyFormProps = {
  isEdit?: boolean;
};

const SurveyWizard: React.FC<SurveyFormProps> = ({ isEdit = false }) => {
  const t = useTranslations("surveyForm");
  const lang = useLocale();
  const role = useRole();
  const profile = useProfile();
  const { post, put, get } = useApiData();
  const [errors, setErrors] = useState<ValidationError[]>([]);
  const [members, setMembers] = useState<OrgUser[]>([]);
  const navigate = useNavigate();
  const { survey } = useSurvey();
  const { currentOrganization, currentRole, subscription } = useOrganizations();
  const [invites, setInvites] = useState<EmailLang[]>([]);
  const orgData = useOrganizationData();
  const [step, setStep] = useState(0);
  const isIndividual = subscription?.type !== "org";

  const [formData, setFormData] = useState<SurveyFormData>({
    id: survey?.id || crypto.randomUUID(),
    endDate: survey?.endDate || new Date(Date.now() + 7 * 24 * 3600000).toISOString().substring(0, 10),
    invitationText: survey?.invitationText || "",
    surveyName: survey?.surveyName || "",
    defaultLang: lang || "en",
    accessibleBy: isIndividual ? "me" : survey?.accessibleBy || orgData?.allowPrivateSurveys === 1 ? "me" : "supporters",
    onBehalfOf: survey?.onBehalfOf || profile?.userid || null,
    groupsWithAccess: survey?.groupsWithAccess || [],
    surveyType: survey?.surveyType || "long-term",
    allowAnonymous: survey?.allowAnonymous || false,
    showRecommendation: survey?.showRecommendation ?? true,
  });

  useEffect(() => {
    const getUsers = async () => {
      const fetchedMembers = await get(`/organization/${currentOrganization}/users`);
      if (fetchedMembers) {
        setMembers(fetchedMembers);
      }
    };
    if (role === "supporter" || role === "owner") {
      getUsers();
    }
  }, [role, get, currentOrganization]);

  const validateStep = (stepIndex: number): ValidationError[] => {
    const nextErrors: ValidationError[] = [];
    const inOneWeek = new Date(Date.now() + 7 * 24 * 3600000).toISOString().substring(0, 10);

    switch (stepIndex) {
      case 0:
        if (!formData.surveyName) nextErrors.push({ name: "surveyName", message: t("fieldRequired") });
        if (!formData.invitationText) nextErrors.push({ name: "invitationText", message: t("fieldRequired") });
        break;
      case 3:
        if (!formData.endDate) nextErrors.push({ name: "endDate", message: t("fieldRequired") });
        if (!isEdit && formData.endDate < inOneWeek) nextErrors.push({ name: "endDate", message: t("minimumDuration") });
        if (!formData.defaultLang) nextErrors.push({ name: "defaultLang", message: t("fieldRequired") });
        if (!formData.accessibleBy) nextErrors.push({ name: "accessibleBy", message: t("fieldRequired") });
        if (formData.accessibleBy === "selectedGroups" && (!formData.groupsWithAccess || formData.groupsWithAccess.length === 0)) {
          nextErrors.push({ name: "groupsWithAccess", message: t("selectAtLeastOneGroup") });
        }
        break;
      default:
        break;
    }
    return nextErrors;
  };

  const validateWholeModel = (): ValidationError[] => {
    let allErrors: ValidationError[] = [];
    for (let i = 0; i < steps.length; i += 1) {
      allErrors = [...allErrors, ...validateStep(i)];
    }
    return allErrors;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name: field } = e.target;
    const value = e.target instanceof HTMLInputElement && e.target.type === "checkbox" ? e.target.checked : e.target.value;

    setFormData(
      (prevData) =>
        ({
          ...prevData,
          [field]: value,
          accessibleBy: field === "onBehalfOf" && value !== profile?.userid ? "supporters" : field === "accessibleBy" ? value : prevData.accessibleBy,
          groupsWithAccess: field === "accessibleBy" && value !== "selectedGroups" ? [] : prevData.groupsWithAccess,
        }) as SurveyFormData,
    );
    setErrors(errors.filter((error) => error.name !== field));
  };

  const setGroupsWithAccess = (groups: string[]) => {
    setFormData((prevData) => ({ ...prevData, groupsWithAccess: groups }));
    setErrors(errors.filter((error) => error.name !== "groupsWithAccess"));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const allErrors = validateWholeModel();
    if (allErrors.length > 0) {
      setErrors(allErrors);
      return;
    }

    const result = isEdit ? await put(`/survey/${formData.id}`, formData) : await post(`/survey/${formData.id}`, formData);

    if (!result) {
      setErrors([{ name: "form", message: t("serverError") }]);
    } else if (isEdit) {
      window.location.reload();
    } else {
      const sendInvitationTo = invites.filter((i) => i.email !== "");
      if (sendInvitationTo.length > 0) {
        await post(`/response/${result.id}/responders`, sendInvitationTo);
      }
      navigate(`/survey/${result.id}`);
    }
  };

  const getAccessibleByOptions = () => {
    const isOnbehalfOf = formData.onBehalfOf !== null && formData.onBehalfOf !== profile?.userid;
    if (["supporter", "owner"].includes(currentRole) && isOnbehalfOf) {
      return [
        { value: "supporters", text: t("accessibleBySupporters") },
        { value: "selectedGroups", text: t("accessibleBySelectedGroups") },
      ];
    }

    const result = [
      { value: "supporters", text: t("accessibleBySupporters") },
      { value: "selectedGroups", text: t("accessibleBySelectedGroups") },
      { value: "org", text: t("accessibleByOrganization") },
    ];

    return orgData?.allowPrivateSurveys ? [{ value: "me", text: t("accessibleByMe") }, ...result] : result;
  };

  const steps = [
    {
      content: (
        <>
          <div className={formStyles.fieldRow}>
            <Input
              name='surveyName'
              maxLength={MAX_SURVEY_NAME}
              handleChange={handleChange}
              label={t("surveyName")}
              value={formData.surveyName}
              error={errors.find((e) => e.name === "surveyName")}
            />

            <Select
              options={[
                { value: "long-term", text: t("surveyTypeLongTerm") },
                { value: "workshop", text: t("surveyTypeWorkshop") },
                { value: "delivery", text: t("surveyTypeDelivery") },
              ]}
              handleChange={handleChange}
              name='surveyType'
              label={t("surveyTypeLabel")}
              value={formData.surveyType}
              helpText={t("help.surveyType")}
              error={errors.find((e) => e.name === "surveyType")}
            />
          </div>
          <Tip>
            <div className={styles.tipCopy}>
              <p className={styles.tipParagraph}>{t("surveyTypeNoChange")}</p>
              <p className={styles.tipParagraph}>{t("selectedSurveyTypeQuestions")}</p>
              <ol>
                {getQuestionsForSurveyType(formData.surveyType).map((q) => (
                  <li key={q}>{t(`questions.${q}`, profile?.candidate)}</li>
                ))}
              </ol>
            </div>
          </Tip>

          <div className={formStyles.fieldRow}>
            <Input
              type='textarea'
              name='invitationText'
              maxLength={MAX_INVITATION_TEXT}
              handleChange={handleChange}
              label={t("invitationText")}
              value={formData.invitationText}
              helpText={t("help.invitationText")}
              error={errors.find((e) => e.name === "invitationText")}
            />
          </div>
        </>
      ),
    },
    {
      title: t("emailPreviewTitle"),
      infoText: t("emailPreviewInfo"),
      content: <EmailPreview invitationText={formData.invitationText} lang={lang} name={profile?.candidate || ""} />,
    },
    {
      title: t("invitesHeading"),
      infoText: t("invitesInfo"),
      content: (
        <>
          <Tip>{t("skipForLater")}</Tip>
          <Invites asForm={false} onInvitesUpdated={setInvites} displayInvitationText={false} initialEmails={invites} displayTip={false} />
          <Input
            type='checkbox'
            error={errors.find((e) => e.name === "allowAnonymous")}
            name='allowAnonymous'
            handleChange={handleChange}
            label={t("allowAnonymous")}
            value={formData.allowAnonymous}
            infoText={t("allowAnonymousInfo")}
          />
          <Input
            type='checkbox'
            name='showRecommendation'
            handleChange={handleChange}
            label={t("showRecommendation")}
            value={formData.showRecommendation}
            infoText={t("showRecommendationInfo")}
          />
        </>
      ),
    },
    {
      title: t("surveyAccessStep"),
      content: (
        <>
          <div className={formStyles.fieldRow}>
            <Input
              type='date'
              name='endDate'
              handleChange={handleChange}
              label={t("endDate")}
              helpText={t("help.endDate")}
              value={formData.endDate}
              error={errors.find((e) => e.name === "endDate")}
            />
            {members && members.length > 0 && (role === "supporter" || role === "owner") && (
              <Select
                options={members.map((m) => ({ value: `${m.userId}`, text: `${m.candidate}` }))}
                handleChange={handleChange}
                name='onBehalfOf'
                helpText={t("help.onBehalfOf")}
                label={t("onBehalfOf")}
                value={formData.onBehalfOf || ""}
              />
            )}
          </div>
          {!isIndividual && (
            <>
              <div className={formStyles.fieldRow}>
                <Select
                  options={getAccessibleByOptions()}
                  readOnly={!currentOrganization}
                  handleChange={handleChange}
                  name='accessibleBy'
                  label={t("accessibleBy")}
                  helpText={t("help.accessibleBy")}
                  value={formData.accessibleBy}
                  error={errors.find((e) => e.name === "accessibleBy")}
                />
              </div>
              {formData.accessibleBy === "selectedGroups" && (
                <GroupAccess initiallySelected={formData.groupsWithAccess || []} setGroupsWithAccess={setGroupsWithAccess} />
              )}
            </>
          )}
        </>
      ),
    },
  ];

  const nextStep = () => {
    const stepErrors = validateStep(step);
    if (stepErrors.length === 0) {
      setStep((prevStep) => Math.min(prevStep + 1, steps.length - 1));
    } else {
      setErrors(stepErrors);
    }
  };

  const prevStep = () => setStep((prevStep) => Math.max(prevStep - 1, 0));

  return (
    <Section span={8}>
      <HeadingWithInfo level={1} heading={isEdit ? t("editHeading") : t("heading")} info={<SurveyInfo />} />
      <form className={styles.form} data-testid='survey-wizard-form' onSubmit={handleSubmit}>
        {steps[step].title &&
          (steps[step].infoText ? <HeadingWithInfo level={2} heading={steps[step].title || ""} info={steps[step].infoText} /> : <H2>{steps[step].title}</H2>)}
        {steps[step].content}

        <WizardProgress max={steps.length} value={step + 1} />
        <div className={styles.actions}>
          {step > 0 ? <Button onClick={prevStep} testId='survey-wizard-previous'>{t("previous")}</Button> : <div />}
          {step < steps.length - 1 ? (
            <Button onClick={nextStep} testId='survey-wizard-next'>
              {t("next")}
            </Button>
          ) : (
            <Button testId='survey-wizard-submit' type='submit'>
              {t(isEdit ? "submitEdit" : "submit")}
            </Button>
          )}
        </div>
      </form>
    </Section>
  );
};

export default SurveyWizard;
